import React from 'react';
import { money } from '../../utils/helpers';
import { LINE_SOURCES, CHARGE_RULES, sourceOf, lineCountText, linesSummary } from '../../utils/costLines';

// Desktop grid of the lines of one booking: one row per line, worked out live
// (lineInfo = lineMath with the event's counts). Logic lives in the owner's
// hook (setLine / addLine / removeLine). A Fixed line is just an amount; a Qty
// line is quantity × rate × days, the days counted from the dates when given,
// and the quantity may follow a live count (Persons / Households / Cards).
export default function CostLinesEditorDesktop({ lines, setLine, addLine, removeLine, lineInfo, functions, categoryList }) {
  const fnOptions = (functions || []).map(f => ({ value: String(f.FunctionId), label: f.FunctionName }));
  // Two totals, because a quotation has two: what is owed if nothing more is
  // taken, and what it comes to if every "if needed" line is taken and every
  // range goes to its ceiling.
  const sum = linesSummary(lines, null);
  const total = lines.reduce((s, l) => s + (lineInfo(l).optional ? 0 : lineInfo(l).amount), 0);
  return (
    <div className="mn-lines">
      <table className="tbl compact mn-lines-tbl">
        <thead>
          <tr>
            <th>Item *</th><th>Function</th><th>Type</th><th className="mn-r">Qty / min</th><th className="mn-r">Rate ₹</th>
            <th>Stay / days</th><th>Quantity follows</th><th className="mn-r" title="The top of a range - varmala 4,000 to 6,000. Blank = the price is firm.">Up to ₹</th>
            <th title="Taken only if needed - it is listed and shown in the &quot;up to&quot; total, never in what is owed">If needed</th>
            <th className="mn-r">Amount</th><th></th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l, i) => {
            const m = lineInfo(l);
            const fixed = l.Basis === 'Fixed';
            const src = sourceOf(l.LiveSource);
            const countText = lineCountText(l, m);
            return (
              <React.Fragment key={l.key}>
                <tr>
                  <td>
                    <input className="mn-line-title" autoFocus={!l.Title && i === lines.length - 1 && i > 0} value={l.Title}
                      placeholder={i === 0 ? 'e.g. Deluxe room' : ''} onChange={e => setLine(l.key, 'Title', e.target.value)} />
                    <input className="mn-line-cat" list={categoryList} value={l.Category} placeholder="Category"
                      title="Blank = the expense's category" onChange={e => setLine(l.key, 'Category', e.target.value)} />
                  </td>
                  <td>
                    <select value={l.FunctionId} onChange={e => setLine(l.key, 'FunctionId', e.target.value)}>
                      <option value="">Whole event</option>
                      {fnOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </td>
                  <td>
                    <select value={l.Basis} onChange={e => setLine(l.key, 'Basis', e.target.value)}>
                      <option value="Qty">Qty × rate</option>
                      <option value="Fixed">Fixed</option>
                    </select>
                  </td>
                  {fixed ? (
                    <>
                      <td className="mn-r" colSpan={2}>
                        <input className="mn-num w110" inputMode="decimal" placeholder="Amount" value={l.Amount}
                          onChange={e => setLine(l.key, 'Amount', e.target.value)} />
                      </td>
                      <td colSpan={2} className="muted">—</td>
                    </>
                  ) : (
                    <>
                      <td className="mn-r">
                        <input className="mn-num w70" inputMode="decimal" value={l.Quantity}
                          placeholder={m.linked ? String(m.live) : ''} onChange={e => setLine(l.key, 'Quantity', e.target.value)} />
                      </td>
                      <td className="mn-r">
                        <input className="mn-num w90" inputMode="decimal" value={l.Rate} onChange={e => setLine(l.key, 'Rate', e.target.value)} />
                      </td>
                      <td className="mn-stay">
                        <input type="date" value={l.FromDate} title="Check-in / from" onChange={e => setLine(l.key, 'FromDate', e.target.value)} />
                        <input type="date" value={l.ToDate} title="Check-out / to" onChange={e => setLine(l.key, 'ToDate', e.target.value)} />
                        {l.FromDate && l.ToDate ? (
                          <span className="mn-days">{m.days} night{m.days === 1 ? '' : 's'}</span>
                        ) : (
                          <label className="mn-days">× <input className="mn-num w40" inputMode="numeric" value={l.Days}
                            onChange={e => setLine(l.key, 'Days', e.target.value)} /> d</label>
                        )}
                      </td>
                      <td>
                        <select value={l.LiveSource} onChange={e => setLine(l.key, 'LiveSource', e.target.value)}>
                          {LINE_SOURCES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>
                        {src.value && (
                          <select value={l.ChargeRule} onChange={e => setLine(l.key, 'ChargeRule', e.target.value)}>
                            {CHARGE_RULES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                          </select>
                        )}
                      </td>
                    </>
                  )}
                  {/* "As per requirement": the floor is the price, this is the ceiling. */}
                  <td className="mn-r">
                    <input className="mn-num w90" inputMode="decimal" value={l.RateMax} placeholder="—"
                      title={fixed ? 'The most this can come to' : 'The highest rate per piece'}
                      onChange={e => setLine(l.key, 'RateMax', e.target.value)} />
                  </td>
                  <td className="mn-c">
                    <input type="checkbox" checked={Number(l.Optional || 0) === 1}
                      title="Taken only if needed - not counted in what is owed"
                      onChange={e => setLine(l.key, 'Optional', e.target.checked ? 1 : 0)} />
                  </td>
                  <td className={'mn-r' + (m.optional ? ' muted' : '')}>
                    <strong>{money(m.amount)}</strong>
                    {m.ranged && <div className="muted" style={{ fontSize: 11 }}>up to {money(m.maxAmount)}</div>}
                  </td>
                  <td><button type="button" className="icon-btn danger" title="Remove this line" onClick={() => removeLine(l.key)}>✕</button></td>
                </tr>
                {(countText || (src.needsFunction && !l.FunctionId)) && (
                  <tr className="mn-line-note">
                    <td colSpan={11}>
                      {src.needsFunction && !l.FunctionId
                        ? <span className="mn-due">Pick the function whose {src.unit} this follows</span>
                        : <>{countText} → <strong>{m.qty} × {money(l.Rate)}{m.days > 1 ? ` × ${m.days}` : ''}</strong></>}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={9}>
              <button type="button" className="btn light sm" onClick={() => addLine()}>+ Line</button>
              <button type="button" className="btn light sm" onClick={() => addLine({ Basis: 'Fixed', FromDate: '', ToDate: '' })}>+ Fixed amount</button>
              <button type="button" className="btn light sm" title="A line taken only if needed - a CO2 gun, paper effects"
                onClick={() => addLine({ Basis: 'Fixed', FromDate: '', ToDate: '', Optional: 1 })}>+ If needed</button>
            </td>
            <td className="mn-r">
              <strong>{money(total)}</strong>
              {sum.upTo > total && <div className="muted" style={{ fontSize: 11 }}>up to {money(sum.upTo)}</div>}
            </td>
            <td></td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
